import React, { useState, useEffect } from "react";
import { db, auth } from "../firebaseConfig";
import { collection, getDocs, getDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { toast } from "react-toastify";
import { getFeatureAccess } from "../utils/useAccessControl";
import "../styles/AdminTable.css";

const AdminTable = () => {
  const [data, setData] = useState({ users: [], vehicles: [], transactions: [], registrations: [], supportTickets: [] });
  const [activeTab, setActiveTab] = useState("users");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const tier = userDoc.exists() ? userDoc.data().tier || "basic" : "basic";
        setIsAdmin(getFeatureAccess(tier).createUser === true);
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        const vehiclesSnapshot = await getDocs(collection(db, "vehicles"));
        const transactionsSnapshot = await getDocs(collection(db, "transactions"));
        const registrationsSnapshot = await getDocs(collection(db, "registrations"));
        const supportTicketsSnapshot = await getDocs(collection(db, "supportTickets"));
        setData({
          users: usersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          vehicles: vehiclesSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          transactions: transactionsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          registrations: registrationsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          supportTickets: supportTicketsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data!");
      }
    };
    if (isAdmin) fetchData();
  }, [isAdmin]);

  const handleEdit = async (collectionName, id, updatedData) => {
    try {
      await updateDoc(doc(db, collectionName, id), updatedData);
      toast.success("Record updated successfully!");
      setData((prev) => ({
        ...prev,
        [collectionName]: prev[collectionName].map((item) =>
          item.id === id ? { ...item, ...updatedData } : item
        ),
      }));
    } catch (error) {
      console.error("Error updating record:", error);
      toast.error("Failed to update record!");
    }
  };

  const handleDelete = async (collectionName, id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        await deleteDoc(doc(db, collectionName, id));
        toast.success("Record deleted successfully!");
        setData((prev) => ({
          ...prev,
          [collectionName]: prev[collectionName].filter((item) => item.id !== id),
        }));
      } catch (error) {
        console.error("Error deleting record:", error);
        toast.error("Failed to delete record!");
      }
    }
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      const aValue = a[sortField] || "";
      const bValue = b[sortField] || "";
      if (sortOrder === "asc") return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });
  };

  const filterData = (data) => {
    return data.filter((item) =>
      Object.values(item).some((val) =>
        val?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  if (loading) return <p>Loading...</p>;
  if (!isAdmin) return <p>Access denied. Admin only.</p>;

  const collections = {
    users: [
      { key: "email", label: "Email" },
      { key: "name", label: "Name" },
      { key: "tier", label: "Tier" },
      { key: "createdAt", label: "Registered" },
    ],
    vehicles: [
      { key: "plateNumber", label: "Plate Number" },
      { key: "imei", label: "IMEI" },
      { key: "deviceId", label: "Device ID" },
      { key: "ownerUid", label: "Owner UID" },
    ],
    transactions: [
      { key: "reference", label: "Reference" },
      { key: "amount", label: "Amount" },
      { key: "plan", label: "Plan" },
      { key: "paidAt", label: "Paid At" },
    ],
    registrations: [
      { key: "email", label: "Email" },
      { key: "createdAt", label: "Registered At" },
    ],
    supportTickets: [
      { key: "ticketId", label: "Ticket ID" },
      { key: "status", label: "Status" },
      { key: "createdAt", label: "Created At" },
    ],
  };

  const currentData = filterData(sortData(data[activeTab]));

  return (
    <div className="admin-table-container">
      <h2>Admin Dashboard</h2>
      <div className="tabs">
        {Object.keys(collections).map((tab) => (
          <button
            key={tab}
            className={activeTab === tab ? "active" : ""}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
      <input
        type="text"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="search-bar"
      />
      <div className="sort-controls">
        <select onChange={(e) => setSortField(e.target.value)} value={sortField}>
          {collections[activeTab].map((col) => (
            <option key={col.key} value={col.key}>
              {col.label}
            </option>
          ))}
        </select>
        <button onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}>
          {sortOrder === "asc" ? "↑ Asc" : "↓ Desc"}
        </button>
      </div>
      <table className="admin-table">
        <thead>
          <tr>
            {collections[activeTab].map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentData.map((item) => (
            <tr key={item.id}>
              {collections[activeTab].map((col) => (
                <td key={col.key}>
                  {col.key.includes("At") && item[col.key]
                    ? new Date(item[col.key]).toLocaleString()
                    : item[col.key] || "N/A"}
                </td>
              ))}
              <td>
                <button
                  onClick={() =>
                    handleEdit(activeTab, item.id, promptEdit(item, collections[activeTab]))
                  }
                >
                  Edit
                </button>
                <button onClick={() => handleDelete(activeTab, item.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const promptEdit = (item, columns) => {
  const updated = {};
  columns.forEach((col) => {
    const value = prompt(`Edit ${col.label}`, item[col.key]);
    if (value !== null) updated[col.key] = value;
  });
  return updated;
};

export default AdminTable;